import { mergeBotDirectorySnapshots } from '../bot-chat-directory-projection.js'
import { projectArkmeConversationAttention } from '../conversation-attention.js'
import type { ArkmeChatPinProjection, ArkmeSourceItem, ArkmeSourceList } from '../types.js'
import { retainNewerArkmeChatPolicy } from '../chat-policy-projection.js'
import { arkmeBadgeUnreadCount, projectArkmeChatAttentionFromMuted } from '../chat-attention.js'
import { ArkmeClientError, callArkme } from './api.js'
import { arkmeChatSourceIdentityKey, arkmeSourceIdentityKey } from './source-identity.js'

const DEFAULT_ROOT_CACHE_MAX_AGE_MS = 30_000
const ROOT_DIRECTORY_PAGE_LIMIT = 20
const MAX_ROOT_PAGES = 10
// Current rows have stable keys; retain a bounded recent alias window for in-flight/legacy ACK handles.
const MAX_SOURCE_REF_ALIASES = 40_000
// The first retry follows the Host directory read's two-second failure cooldown.
const ROOT_READ_RETRY_DELAYS_MS = [2_000, 4_000] as const

export type ArkmeClientAccountScope = number | string | undefined

function clientAccountScopeKey(scope: ArkmeClientAccountScope): string | undefined {
  if (typeof scope === 'number') return Number.isSafeInteger(scope) && scope > 0 ? String(scope) : undefined
  const normalized = scope?.trim()
  return normalized === undefined || normalized === '' || normalized.length > 256 ? undefined : normalized
}

interface ArkmeChatDirectoryStoreOptions {
  loadPage?: (cursor?: string, force?: boolean) => Promise<ArkmeSourceList>
  maxAgeMs?: number
  now?: () => number
}

export interface ArkmeChatDirectorySnapshot {
  projection?: ArkmeSourceList["projection"]
  revision: number
  sources: ArkmeSourceItem[]
  baselineReady: boolean
  isRefreshing: boolean
}

export type ArkmeConversationSnapshot = Readonly<ReturnType<typeof projectArkmeConversationAttention>> & {
  visibility: NonNullable<ArkmeSourceList['projection']>['visibility']
  accountScope: string | undefined
  baselineReady: boolean
}

export interface ArkmeChatDirectorySourceUpdate {
  source: ArkmeSourceItem
  sourceKey?: string
}

type ArkmeChatDirectoryMutation =
  | { type: 'upsert'; source: ArkmeSourceItem; sourceKey?: string }
  | { type: 'read-ack'; sourceRef: string; sourceKey?: string; effectiveReadSequence: number; unreadCount: number }

interface ArkmeChatReadTarget {
  sourceRef: string
  sourceKey?: string
  effectiveReadSequence: number
}

interface ArkmeChatReadWatermark {
  effectiveReadSequence: number
  unreadCount: number
}

interface ArkmeChatDirectoryIndexes {
  sourceKeysByRef: Map<string, string>
}

function normalizedCount(value: number): number {
  return Math.max(0, Math.trunc(value))
}

function normalizedSequence(value: number | undefined): number {
  return Number.isSafeInteger(value) && (value ?? 0) > 0 ? value ?? 0 : 0
}

function projectSourceAttention(source: ArkmeSourceItem, unreadCount = source.unreadCount): ArkmeSourceItem {
  if (source.kind !== 'private_chat' && source.kind !== 'group_chat') return source
  return { ...source, ...projectArkmeChatAttentionFromMuted(unreadCount, source.isMuted === true) }
}

function normalizedSourceKey(value: string | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized === undefined || normalized === '' ? undefined : normalized
}

function rememberSourceKey(indexes: ArkmeChatDirectoryIndexes, sourceRef: string, sourceKey: string | undefined): void {
  const normalized = normalizedSourceKey(sourceKey)
  if (normalized !== undefined) {
    indexes.sourceKeysByRef.delete(sourceRef)
    indexes.sourceKeysByRef.set(sourceRef, normalized)
    if (indexes.sourceKeysByRef.size > MAX_SOURCE_REF_ALIASES) indexes.sourceKeysByRef.delete(indexes.sourceKeysByRef.keys().next().value!)
  }
}

function identityForSource(indexes: ArkmeChatDirectoryIndexes, sourceRef: string, sourceKey?: string): string {
  return normalizedSourceKey(sourceKey) ?? indexes.sourceKeysByRef.get(sourceRef) ?? sourceRef
}

function findSourceIndex(
  sources: readonly ArkmeSourceItem[],
  indexes: ArkmeChatDirectoryIndexes,
  sourceRef: string,
  sourceKey?: string,
): number {
  const identity = identityForSource(indexes, sourceRef, sourceKey)
  return sources.findIndex(item => identityForSource(indexes, item.sourceRef, item.sourceKey) === identity)
}

function recordReadWatermark(
  watermarks: Map<string, ArkmeChatReadWatermark>,
  indexes: ArkmeChatDirectoryIndexes,
  mutation: ArkmeChatReadTarget & { unreadCount: number },
): void {
  rememberSourceKey(indexes, mutation.sourceRef, mutation.sourceKey)
  const effectiveReadSequence = normalizedSequence(mutation.effectiveReadSequence)
  if (effectiveReadSequence <= 0) return
  const key = identityForSource(indexes, mutation.sourceRef, mutation.sourceKey)
  const unreadCount = normalizedCount(mutation.unreadCount)
  const existing = watermarks.get(key)
  if (existing === undefined || effectiveReadSequence > existing.effectiveReadSequence) {
    watermarks.set(key, { effectiveReadSequence, unreadCount })
    return
  }
  if (effectiveReadSequence === existing.effectiveReadSequence) {
    watermarks.set(key, {
      effectiveReadSequence,
      unreadCount: Math.min(existing.unreadCount, unreadCount),
    })
  }
}

function applyReadWatermark(
  source: ArkmeSourceItem,
  watermarks: ReadonlyMap<string, ArkmeChatReadWatermark>,
  indexes: ArkmeChatDirectoryIndexes,
  sourceKey?: string,
): ArkmeSourceItem {
  const effectiveSourceKey = sourceKey ?? source.sourceKey
  rememberSourceKey(indexes, source.sourceRef, effectiveSourceKey)
  const watermark = watermarks.get(identityForSource(indexes, source.sourceRef, effectiveSourceKey))
  if (watermark === undefined) return source
  const latestSequence = normalizedSequence(source.latestSequence)
  if (latestSequence > watermark.effectiveReadSequence) return source
  const unreadCount = Math.min(normalizedCount(source.unreadCount), watermark.unreadCount)
  const hasUnreadMention = unreadCount <= 0 && source.hasUnreadMention !== undefined ? false : source.hasUnreadMention
  return unreadCount === source.unreadCount && hasUnreadMention === source.hasUnreadMention
    ? source
    : projectSourceAttention({ ...source, unreadCount, ...(hasUnreadMention === undefined ? {} : { hasUnreadMention }) })
}

function mergeUnreadMention(existing: ArkmeSourceItem | undefined, source: ArkmeSourceItem): boolean | undefined {
  const unreadCount = normalizedCount(source.unreadCount)
  if (unreadCount <= 0) {
    return existing?.hasUnreadMention !== undefined || source.hasUnreadMention !== undefined ? false : undefined
  }
  if (source.kind === 'group_chat') return source.hasUnreadMention ?? existing?.hasUnreadMention
  return source.hasUnreadMention
}

function retainRealtimeSourceMetadata(
  existing: ArkmeSourceItem | undefined,
  source: ArkmeSourceItem,
): ArkmeSourceItem {
  if (existing === undefined) return source
  const avatarRef = source.avatarRef?.trim()
  return {
    ...source,
    ...(source.kind === 'private_chat' && existing.kind === 'private_chat'
      && source.peerUserId === undefined && existing.peerUserId !== undefined
      ? { peerUserId: existing.peerUserId }
      : {}),
    ...(avatarRef === undefined || avatarRef === ''
      ? existing.avatarRef === undefined ? {} : { avatarRef: existing.avatarRef }
      : { avatarRef }),
    ...(source.avatarRefs === undefined || source.avatarRefs.length === 0
      ? existing.avatarRefs === undefined ? {} : { avatarRefs: existing.avatarRefs }
      : {}),
    ...((source.groupAvatar === undefined || source.groupAvatar.slots.length === 0)
      && existing.groupAvatar !== undefined
      ? { groupAvatar: existing.groupAvatar }
      : {}),
  }
}

function mergeSourceProjection(
  existing: ArkmeSourceItem | undefined,
  source: ArkmeSourceItem,
  watermarks: ReadonlyMap<string, ArkmeChatReadWatermark>,
  indexes: ArkmeChatDirectoryIndexes,
  sourceKey?: string,
): ArkmeSourceItem {
  const existingSequence = normalizedSequence(existing?.latestSequence)
  const sourceSequence = normalizedSequence(source.latestSequence)
  // sessions-delta is additive: omitted peer identity and avatar metadata are
  // removed only by a complete directory baseline, which bypasses this merge.
  const sourceWithMetadata = retainRealtimeSourceMetadata(existing, source)
  const merged = existing !== undefined && sourceSequence < existingSequence
    ? (() => {
        const latestPreview = existing.latestPreview ?? sourceWithMetadata.latestPreview
        return {
          ...sourceWithMetadata,
          ...(latestPreview === undefined ? {} : { latestPreview }),
          activeAtMillis: existing.activeAtMillis,
          unreadCount: existing.unreadCount,
          ...(existing.hasUnreadMention === undefined ? {} : { hasUnreadMention: existing.hasUnreadMention }),
          ...(existing.latestSequence === undefined ? {} : { latestSequence: existing.latestSequence }),
        }
      })()
    : {
        ...(() => {
          const hasUnreadMention = mergeUnreadMention(existing, source)
          return {
            ...sourceWithMetadata,
            activeAtMillis: Math.max(existing?.activeAtMillis ?? 0, source.activeAtMillis),
            ...(hasUnreadMention === undefined ? {} : { hasUnreadMention }),
          }
        })(),
      }
  return applyReadWatermark(projectSourceAttention(retainNewerArkmeChatPolicy(existing, merged)), watermarks, indexes, sourceKey)
}

function sourceUpdate(update: ArkmeSourceItem | ArkmeChatDirectorySourceUpdate): ArkmeChatDirectorySourceUpdate {
  if ('source' in update) return update
  return { source: update, ...(update.sourceKey === undefined ? {} : { sourceKey: update.sourceKey }) }
}

function directorySourceIdentity(source: ArkmeSourceItem): string {
  return normalizedSourceKey(source.sourceKey) ?? source.sourceRef
}

type DirectorySourceScalarField = Exclude<keyof ArkmeSourceItem, 'avatarRefs' | 'groupAvatar'>

const DIRECTORY_SOURCE_SCALAR_FIELDS: Record<DirectorySourceScalarField, true> = {
  directMessageAdmissionApplicable: true,
  topicKind: true,
  sourceRef: true,
  sourceKey: true,
  peerUserId: true,
  parentSourceRef: true,
  topicHierarchyKey: true,
  parentTopicHierarchyKey: true,
  hasPendingChildren: true,
  siblingOrder: true,
  kind: true,
  displayName: true,
  avatarRef: true,
  latestPreview: true,
  activeAtMillis: true,
  unreadCount: true,
  badgeUnreadCount: true,
  notificationAllowed: true,
  hasUnreadMention: true,
  isMuted: true,
  isPinned: true,
  chatPolicyUpdatedAtMillis: true,
  chatNotificationPolicyUpdatedAtMillis: true,
  latestSequence: true,
  readSequence: true,
  recordCount: true,
}

function sameOrderedAvatarRefs(left: readonly string[] | undefined, right: readonly string[] | undefined): boolean {
  if (left === right) return true
  return left !== undefined && right !== undefined
    && left.length === right.length
    && left.every((value, index) => value === right[index])
}

function sameGroupAvatarPresentation(
  left: ArkmeSourceItem['groupAvatar'],
  right: ArkmeSourceItem['groupAvatar'],
): boolean {
  if (left === right) return true
  if (left === undefined || right === undefined) return false
  // computedAtMillis is freshness provenance; the remaining structured fields define projection identity.
  if (left.memberCount !== right.memberCount || left.strategy !== right.strategy
    || left.slots.length !== right.slots.length) return false
  return left.slots.every((slot, index) => {
    const other = right.slots[index]
    if (other === undefined || slot.avatarRef !== other.avatarRef || slot.fallback?.kind !== other.fallback?.kind) return false
    if (slot.fallback?.kind !== 'phone_default' || other.fallback?.kind !== 'phone_default') return true
    return slot.fallback.colorIndex === other.fallback.colorIndex && slot.fallback.label === other.fallback.label
  })
}

function sameDirectorySourcePresentation(left: ArkmeSourceItem, right: ArkmeSourceItem): boolean {
  for (const field of Object.keys(DIRECTORY_SOURCE_SCALAR_FIELDS) as DirectorySourceScalarField[]) {
    if (left[field] !== right[field]) return false
  }
  return sameOrderedAvatarRefs(left.avatarRefs, right.avatarRefs)
    && sameGroupAvatarPresentation(left.groupAvatar, right.groupAvatar)
}

function reconcileDirectorySources(
  current: readonly ArkmeSourceItem[],
  incoming: readonly ArkmeSourceItem[],
): ArkmeSourceItem[] | readonly ArkmeSourceItem[] {
  const currentByIdentity = new Map(current.map(source => [directorySourceIdentity(source), source]))
  const reconciled = incoming.map(source => {
    const previous = currentByIdentity.get(directorySourceIdentity(source))
    const projected = retainNewerArkmeChatPolicy(previous, source)
    return previous !== undefined
      && sameDirectorySourcePresentation(previous, projected)
      ? previous
      : projected
  })
  return reconciled.length === current.length && reconciled.every((source, index) => source === current[index])
    ? current
    : reconciled
}

function mergeReadWatermark(
  target: Map<string, ArkmeChatReadWatermark>,
  key: string,
  watermark: ArkmeChatReadWatermark,
): void {
  const existing = target.get(key)
  if (existing === undefined || watermark.effectiveReadSequence > existing.effectiveReadSequence) {
    target.set(key, { ...watermark })
    return
  }
  if (watermark.effectiveReadSequence === existing.effectiveReadSequence) {
    target.set(key, {
      effectiveReadSequence: watermark.effectiveReadSequence,
      unreadCount: Math.min(existing.unreadCount, watermark.unreadCount),
    })
  }
}

function applyDirectoryMutations(
  initialSources: readonly ArkmeSourceItem[],
  mutations: readonly ArkmeChatDirectoryMutation[],
  readWatermarks: Map<string, ArkmeChatReadWatermark>,
  indexes: ArkmeChatDirectoryIndexes,
): ArkmeSourceItem[] {
  let sources = initialSources.map(source => applyReadWatermark(source, readWatermarks, indexes))
  if (mutations.length === 0) return sources
  // Stable-key, activity-ordered directories can apply an entire cached page in one map pass.
  // Preserve the existing legacy ordering path for old entries without a stable key.
  if (sources.every((source, index) => normalizedSourceKey(source.sourceKey) !== undefined
      && (index === 0 || sources[index - 1]!.activeAtMillis >= source.activeAtMillis))
    && mutations.every(mutation => mutation.type === 'read-ack' || normalizedSourceKey(mutation.sourceKey ?? mutation.source.sourceKey) !== undefined)) {
    const byKey = new Map(sources.map(source => [identityForSource(indexes, source.sourceRef, source.sourceKey), source]))
    for (const mutation of mutations) {
      if (mutation.type === 'read-ack') {
        recordReadWatermark(readWatermarks, indexes, mutation)
        const key = identityForSource(indexes, mutation.sourceRef, mutation.sourceKey)
        const current = byKey.get(key)
        if (current !== undefined) byKey.set(key, applyReadWatermark(current, readWatermarks, indexes, mutation.sourceKey))
        continue
      }
      const { source } = mutation
      const sourceKey = mutation.sourceKey ?? source.sourceKey
      rememberSourceKey(indexes, source.sourceRef, sourceKey)
      const key = identityForSource(indexes, source.sourceRef, sourceKey)
      const current = byKey.get(key)
      const normalized = mergeSourceProjection(current, source, readWatermarks, indexes, sourceKey)
      if (current === undefined || normalized.activeAtMillis > current.activeAtMillis) byKey.delete(key)
      byKey.set(key, normalized)
    }
    return [...byKey.values()].sort((left, right) => right.activeAtMillis - left.activeAtMillis)
  }
  for (const mutation of mutations) {
    if (mutation.type === 'read-ack') {
      recordReadWatermark(readWatermarks, indexes, mutation)
      const index = findSourceIndex(sources, indexes, mutation.sourceRef, mutation.sourceKey)
      if (index >= 0) sources[index] = applyReadWatermark(sources[index]!, readWatermarks, indexes, mutation.sourceKey)
      continue
    }
    const source = mutation.source
    const sourceKey = mutation.sourceKey ?? source.sourceKey
    rememberSourceKey(indexes, source.sourceRef, sourceKey)
    const existingIndex = findSourceIndex(sources, indexes, source.sourceRef, sourceKey)
    const existing = existingIndex < 0 ? undefined : sources[existingIndex]
    const normalized = mergeSourceProjection(existing, source, readWatermarks, indexes, sourceKey)
    if (existing !== undefined && normalized.activeAtMillis <= existing.activeAtMillis) {
      sources[existingIndex] = normalized
      continue
    }
    const identity = identityForSource(indexes, source.sourceRef, sourceKey)
    sources = sources.filter(item => identityForSource(indexes, item.sourceRef, item.sourceKey) !== identity)
    const insertionIndex = sources.findIndex(item => item.activeAtMillis < normalized.activeAtMillis)
    sources.splice(insertionIndex < 0 ? sources.length : insertionIndex, 0, normalized)
  }
  return sources
}

export class ArkmeChatDirectoryStore {
  private snapshot: ArkmeChatDirectorySnapshot = { revision: 0, sources: [], baselineReady: false, isRefreshing: false }
  private conversationInputs: {
    sources: ArkmeSourceItem[]
    bots: NonNullable<ArkmeSourceList['projection']>['bots'] | undefined
    visibility: NonNullable<ArkmeSourceList['projection']>['visibility'] | undefined
    accountScope: string | undefined
    baselineReady: boolean
  } | undefined
  private conversationSnapshot: ArkmeConversationSnapshot | undefined
  private readonly listeners = new Set<() => void>()
  private readonly loadPage: (cursor?: string, force?: boolean) => Promise<ArkmeSourceList>
  private readonly maxAgeMs: number
  private readonly now: () => number
  private refreshInFlight: Promise<ArkmeSourceItem[]> | undefined
  private refreshedAtMillis = 0
  private accountScopeKey: string | undefined
  private generation = 0
  private projection: ArkmeSourceList["projection"]
  private localBots: NonNullable<ArkmeSourceList['projection']>['bots'] = []
  private localVisibility: NonNullable<ArkmeSourceList['projection']>['visibility'] = []
  private baselineReady = false
  private isRefreshing = false
  private pendingMutations: ArkmeChatDirectoryMutation[] = []
  private readonly readWatermarks = new Map<string, ArkmeChatReadWatermark>()
  private readonly optimisticReadWatermarks = new Map<string, ArkmeChatReadWatermark>()
  private readonly optimisticUnreadBackups = new Map<string, number>()
  private readonly sourceKeysByRef = new Map<string, string>()

  constructor(options: ArkmeChatDirectoryStoreOptions = {}) {
    this.loadPage = options.loadPage ?? (async (cursor, force) => await callArkme<ArkmeSourceList>('sources.list', {
      directory: 'root', limit: ROOT_DIRECTORY_PAGE_LIMIT, localFirst: true,
      ...(cursor === undefined ? {} : { cursor }), ...(force === true ? { refresh: true } : {}),
    }))
    this.maxAgeMs = Math.max(0, Math.trunc(options.maxAgeMs ?? DEFAULT_ROOT_CACHE_MAX_AGE_MS))
    this.now = options.now ?? Date.now
  }

  readonly getSnapshot = (): ArkmeChatDirectorySnapshot => this.snapshot

  /** Like computed: one bounded cache shared by every consumer, invalidated only by its input references. */
  readonly getConversationSnapshot = (): ArkmeConversationSnapshot => {
    const { sources, projection, baselineReady } = this.snapshot
    const bots = projection?.bots ?? this.localBots
    const visibility = projection?.visibility ?? this.localVisibility
    const previous = this.conversationInputs
    if (previous?.sources === sources && previous.bots === bots
      && previous.visibility === visibility && previous.accountScope === this.accountScopeKey
      && previous.baselineReady === baselineReady) return this.conversationSnapshot!
    this.conversationInputs = { sources, bots, visibility, accountScope: this.accountScopeKey, baselineReady }
    const visible = projectArkmeConversationAttention(sources, bots, visibility)
    this.conversationSnapshot = { ...visible, visibility, accountScope: this.accountScopeKey, baselineReady }
    return this.conversationSnapshot
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  activateAccount(scope: ArkmeClientAccountScope): void {
    const normalized = clientAccountScopeKey(scope)
    if (normalized === this.accountScopeKey) return
    const hadLocalMetadata = this.localBots.length > 0 || this.localVisibility.length > 0
    this.projection = undefined
    this.localBots = []
    this.localVisibility = []
    this.conversationInputs = undefined
    this.conversationSnapshot = undefined
    this.accountScopeKey = normalized
    this.generation += 1
    this.refreshInFlight = undefined
    this.refreshedAtMillis = 0
    this.baselineReady = false
    this.isRefreshing = false
    this.pendingMutations = []
    this.readWatermarks.clear()
    this.optimisticReadWatermarks.clear()
    this.optimisticUnreadBackups.clear()
    this.sourceKeysByRef.clear()
    if (this.snapshot.sources.length > 0 || this.snapshot.baselineReady || this.snapshot.isRefreshing || hadLocalMetadata) this.commit([])
  }

  async refreshRoot(options: { force?: boolean; silent?: boolean } = {}): Promise<ArkmeSourceItem[]> {
    if (options.force !== true && this.refreshedAtMillis > 0
      && this.now() - this.refreshedAtMillis < this.maxAgeMs) {
      return [...this.snapshot.sources]
    }
    if (this.refreshInFlight !== undefined) {
      if (options.silent !== true) this.setRefreshing(true)
      return await this.refreshInFlight
    }
    if (options.silent !== true) this.setRefreshing(true)
    const generation = this.generation
    const pending = (async () => {
      const loaded: ArkmeSourceItem[] = []
      const seen = new Set<string>()
      let cursor: string | undefined
      for (let pageIndex = 0; pageIndex < MAX_ROOT_PAGES; pageIndex += 1) {
        const page = await this.loadRootPage(cursor, options.force === true, generation)
        if (generation !== this.generation) return [...this.snapshot.sources]
        if (page === undefined) return [...this.snapshot.sources]
        if (page.projection !== undefined) {
          this.applyHostPage(page)
          this.refreshedAtMillis = this.now()
          return [...this.snapshot.sources]
        }
        for (const source of page.items) {
          const identity = arkmeSourceIdentityKey(source)
          if (seen.has(identity)) continue
          seen.add(identity)
          loaded.push(source)
        }
        if (!page.hasMore || page.nextCursor === undefined) break
        cursor = page.nextCursor
      }
      this.refreshedAtMillis = this.now()
      this.publish(loaded)
      return [...this.snapshot.sources]
    })()
    this.refreshInFlight = pending
    try {
      return await pending
    } finally {
      if (this.refreshInFlight === pending) {
        this.refreshInFlight = undefined
        this.setRefreshing(false)
      }
    }
  }

  /** Host pages are upserts; neither cached snapshots nor later pages replace local live rows. */
  applyHostPage(page: ArkmeSourceList): void {
    const projection = page.projection
    if (projection === undefined) return
    if (this.projection !== undefined && projection.revision < this.projection.revision) {
      const known = new Set(this.snapshot.sources.map(arkmeSourceIdentityKey))
      const removed = new Set(this.projection.removedSourceKeys ?? [])
      const missing = page.items.filter(item => !known.has(arkmeSourceIdentityKey(item)) && !removed.has(arkmeSourceIdentityKey(item)))
      const visibility = new Map(this.projection.visibility.map(item => [`${item.entryKind}:${item.entryRef}`, item]))
      const count = visibility.size
      for (const item of projection.visibility) {
        const key = `${item.entryKind}:${item.entryRef}`
        if (!visibility.has(key)) visibility.set(key, item)
      }
      this.projection = { ...this.projection, visibility: visibility.size === count ? this.projection.visibility : [...visibility.values()] }
      if (missing.length > 0) this.upsertMany(missing)
      else if (visibility.size !== count) this.commit(this.snapshot.sources)
      return
    }
    const visibility = new Map((this.projection?.visibility ?? this.localVisibility).map(item => [`${item.entryKind}:${item.entryRef}`, item]))
    let visibilityChanged = false
    const previousByKey = new Map<string, ArkmeSourceItem>()
    if (page.items.length > 0) for (const source of this.snapshot.sources) {
      const key = arkmeSourceIdentityKey(source)
      if (!previousByKey.has(key)) previousByKey.set(key, source)
    }
    for (const incoming of page.items) {
      const previous = previousByKey.get(arkmeSourceIdentityKey(incoming))
      if (previous !== undefined && previous.sourceRef !== incoming.sourceRef) {
        visibilityChanged = visibility.delete(`source:${previous.sourceRef}`) || visibilityChanged
      }
    }
    for (const item of projection.visibility) {
      const key = `${item.entryKind}:${item.entryRef}`
      if (visibility.get(key)?.hidden !== item.hidden) visibilityChanged = true
      visibility.set(key, item)
    }
    const previousProjection = this.projection
    const inheritedBots = previousProjection?.bots ?? this.localBots
    const mergedBots = mergeBotDirectorySnapshots(inheritedBots, projection.bots, projection.removedBotRefs)
    this.projection = { ...projection, bots: mergedBots, visibility: !visibilityChanged && previousProjection !== undefined
      ? previousProjection.visibility : [...visibility.values()] }
    for (const field of ['botPinnedKeys', 'arkoProfile', 'arkoPreview', 'sendToSelf'] as const) {
      if (previousProjection?.[field] !== undefined && JSON.stringify(previousProjection[field]) === JSON.stringify(projection[field])) Object.assign(this.projection, { [field]: previousProjection[field] })
    }
    this.retainBotVisibility(inheritedBots, mergedBots)
    if (JSON.stringify(mergedBots) === JSON.stringify(inheritedBots)) this.projection!.bots = inheritedBots
    this.localBots = []
    this.localVisibility = []
    const mutations = page.items.map(source => ({ type: 'upsert' as const, source, ...(source.sourceKey === undefined ? {} : { sourceKey: source.sourceKey }) }))
    const removed = new Set(projection.removedSourceKeys ?? [])
    const sources = applyDirectoryMutations(this.snapshot.sources.filter(source => !removed.has(arkmeSourceIdentityKey(source))), [...mutations, ...this.pendingMutations.filter(mutation => mutation.type !== 'upsert' || !removed.has(arkmeSourceIdentityKey(mutation.source)))], this.combinedReadWatermarks(), { sourceKeysByRef: this.sourceKeysByRef })
    const sourceIndexes = new Map<string, number>()
    if (page.items.length > 0) sources.forEach((source, index) => {
      const key = arkmeSourceIdentityKey(source)
      if (!sourceIndexes.has(key)) sourceIndexes.set(key, index)
    })
    for (const incoming of page.items) {
      const index = sourceIndexes.get(arkmeSourceIdentityKey(incoming))
      if (index === undefined) continue
      if (incoming.avatarRef === '') sources[index] = { ...sources[index]!, avatarRef: '' }
      if (incoming.avatarRefs?.length === 0) {
        sources[index] = { ...sources[index]!, avatarRefs: [] }
        if (incoming.groupAvatar === undefined) delete sources[index]!.groupAvatar
      }
    }
    this.pendingMutations = []
    this.baselineReady = true
    this.commit([...reconcileDirectorySources(this.snapshot.sources, sources)])
  }

  private async loadRootPage(cursor: string | undefined, force: boolean, generation: number): Promise<ArkmeSourceList | undefined> {
    for (let attempt = 0; ; attempt += 1) {
      if (generation !== this.generation) return undefined
      try {
        return await this.loadPage(cursor, force)
      } catch (error) {
        if (generation !== this.generation) return undefined
        const delay = ROOT_READ_RETRY_DELAYS_MS[attempt]
        if (!(error instanceof ArkmeClientError) || !error.body.retryable || error.body.recovery?.owner === 'host' || delay === undefined) throw error
        await new Promise<void>(resolve => { setTimeout(resolve, delay) })
      }
    }
  }

  /** Retain visible rows while preventing older reads from satisfying a new owner invalidation. */
  invalidateRoot(): void {
    this.generation += 1
    this.refreshInFlight = undefined
    this.refreshedAtMillis = 0
    this.setRefreshing(false)
  }

  /** A reconnect pin projection is not a directory membership or message snapshot. */
  reconcilePins(pins: readonly ArkmeChatPinProjection[]): void {
    const byKey = new Map<string, ArkmeChatPinProjection>()
    for (const pin of pins) {
      const previous = byKey.get(pin.sourceKey)
      if (previous === undefined || previous.policyUpdatedAtMillis < pin.policyUpdatedAtMillis) byKey.set(pin.sourceKey, pin)
    }
    let changed = false
    const sources = this.snapshot.sources.map(source => {
      if ((source.kind !== 'private_chat' && source.kind !== 'group_chat') || source.sourceKey === undefined) return source
      const pin = byKey.get(source.sourceKey)
      if (pin === undefined) return source
      const projected = retainNewerArkmeChatPolicy(source, {
        ...source, isPinned: pin.pinned, chatPolicyUpdatedAtMillis: pin.policyUpdatedAtMillis,
      })
      if (source.isPinned === projected.isPinned && source.chatPolicyUpdatedAtMillis === projected.chatPolicyUpdatedAtMillis) return source
      changed = true
      return projected
    })
    if (changed) this.commit(sources)
  }

  confirmPin(source: ArkmeSourceItem, pinned: boolean, policyUpdatedAtMillis: number): void {
    if (source.kind !== 'private_chat' && source.kind !== 'group_chat') return
    const targetKey = arkmeSourceIdentityKey(source)
    const current = this.snapshot.sources.find(item => arkmeSourceIdentityKey(item) === targetKey)
    if (current === undefined) return
    const updated = retainNewerArkmeChatPolicy(current, {
      ...current, isPinned: pinned, chatPolicyUpdatedAtMillis: policyUpdatedAtMillis,
    })
    if (updated.chatPolicyUpdatedAtMillis !== policyUpdatedAtMillis) return
    this.refreshedAtMillis = 0
    this.commit(this.snapshot.sources.map(item =>
      arkmeSourceIdentityKey(item) === targetKey ? updated : item))
  }

  publish(sources: ArkmeSourceItem[]): void {
    const merged = applyDirectoryMutations(
      sources,
      this.pendingMutations,
      this.combinedReadWatermarks(),
      { sourceKeysByRef: this.sourceKeysByRef },
    )
    const reconciled = reconcileDirectorySources(this.snapshot.sources, merged)
    const baselineChanged = !this.baselineReady
    this.pendingMutations = []
    this.baselineReady = true
    if (baselineChanged || reconciled !== this.snapshot.sources) this.commit([...reconciled])
  }

  private commit(sources: ArkmeSourceItem[]): void {
    const sameSources = sources.length === this.snapshot.sources.length
      && sources.every((source, index) => source === this.snapshot.sources[index])
    if (!sameSources) {
      const currentVisibility = this.projection?.visibility ?? this.localVisibility
      const oldVisibility = this.snapshot.projection?.visibility ?? currentVisibility
      const previousByKey = new Map(this.snapshot.sources.map(source => [directorySourceIdentity(source), source]))
      let visibility: Map<string, NonNullable<ArkmeSourceList['projection']>['visibility'][number]> | undefined
      let previousVisibility: typeof visibility
      for (const source of sources) {
        const previous = previousByKey.get(directorySourceIdentity(source))
        if (previous === undefined || previous.kind !== source.kind || previous.sourceRef === source.sourceRef) continue
        visibility ??= new Map(currentVisibility.map(item => [`${item.entryKind}:${item.entryRef}`, item]))
        previousVisibility ??= new Map(oldVisibility.map(item => [`${item.entryKind}:${item.entryRef}`, item]))
        const oldKey = `source:${previous.sourceRef}`
        const newKey = `source:${source.sourceRef}`
        const confirmed = previousVisibility.get(oldKey)
        // sourceRef includes message activity, so a new message rotates it without changing conversation identity.
        // Retain its confirmed visibility until an explicit current-ref receipt overrides it; never infer "visible".
        if (!visibility.has(newKey) && confirmed !== undefined) visibility.set(newKey, { ...confirmed, entryRef: source.sourceRef })
        visibility.delete(oldKey)
      }
      if (visibility !== undefined) {
        if (this.projection === undefined) this.localVisibility = [...visibility.values()]
        else this.projection = { ...this.projection, visibility: [...visibility.values()] }
      }
    }
    this.snapshot = {
      ...(this.projection === undefined ? {} : { projection: this.projection }),
      revision: this.snapshot.revision + 1,
      sources: sameSources ? this.snapshot.sources : [...sources],
      baselineReady: this.baselineReady,
      isRefreshing: this.isRefreshing,
    }
    for (const listener of this.listeners) listener()
  }

  upsert(source: ArkmeSourceItem, sourceKey?: string): void {
    this.upsertMany([{ source, ...(sourceKey === undefined ? {} : { sourceKey }) }])
  }

  upsertMany(updates: Array<ArkmeSourceItem | ArkmeChatDirectorySourceUpdate>): void {
    const removed = new Set(this.projection?.removedSourceKeys ?? [])
    const mutations = updates.filter(update => !removed.has(arkmeSourceIdentityKey(sourceUpdate(update).source))).map(update => {
      const normalized = sourceUpdate(update)
      return {
        type: 'upsert' as const,
        source: normalized.source,
        ...(normalized.sourceKey === undefined ? {} : { sourceKey: normalized.sourceKey }),
      }
    })
    if (!this.baselineReady) {
      this.pendingMutations.push(...mutations)
      return
    }
    this.commit(applyDirectoryMutations(
      this.snapshot.sources,
      mutations,
      this.combinedReadWatermarks(),
      { sourceKeysByRef: this.sourceKeysByRef },
    ))
  }

  unreadCount(sourceRef: string): number {
    const indexes = { sourceKeysByRef: new Map(this.sourceKeysByRef) }
    const sources = applyDirectoryMutations(this.snapshot.sources, this.pendingMutations, this.combinedReadWatermarks(), indexes)
    const identity = identityForSource(indexes, sourceRef)
    return sources.find(item => identityForSource(indexes, item.sourceRef) === identity)?.unreadCount ?? 0
  }

  totalUnreadCount(options: { excludeMuted?: boolean } = {}): number {
    const sources = applyDirectoryMutations(
      this.snapshot.sources,
      this.pendingMutations,
      this.combinedReadWatermarks(),
      { sourceKeysByRef: new Map(this.sourceKeysByRef) },
    )
    return sources.reduce((sum, source) => options.excludeMuted === true && source.isMuted === true
      ? sum
      : sum + normalizedCount(source.unreadCount), 0)
  }

  private retainBotVisibility(previousBots: import('../types.js').ArkmeBotSummary[], bots: import('../types.js').ArkmeBotSummary[]): void {
    const nextByKey = new Map(bots.map(bot => [bot.directoryKey || bot.botRef, bot]))
    let visibility: Map<string, NonNullable<ArkmeSourceList['projection']>['visibility'][number]> | undefined
    for (const previous of previousBots) {
      const next = nextByKey.get(previous.directoryKey || previous.botRef)
      if (next === undefined || next.botRef === previous.botRef) continue
      visibility ??= new Map((this.projection?.visibility ?? this.localVisibility).map(item => [`${item.entryKind}:${item.entryRef}`, item]))
      const oldKey = `bot:${previous.botRef}`, newKey = `bot:${next.botRef}`
      const confirmed = visibility.get(oldKey)
      if (confirmed !== undefined && !visibility.has(newKey)) visibility.set(newKey, { ...confirmed, entryRef: next.botRef })
      visibility.delete(oldKey)
    }
    if (visibility === undefined) return
    if (this.projection === undefined) this.localVisibility = [...visibility.values()]
    else this.projection = { ...this.projection, visibility: [...visibility.values()] }
  }

  updateBots(update: import('../types.js').ArkmeBotSummary[] | ((current: import('../types.js').ArkmeBotSummary[]) => import('../types.js').ArkmeBotSummary[])): void {
    const current = this.projection?.bots ?? this.localBots
    const next = mergeBotDirectorySnapshots([], typeof update === 'function' ? update(current) : update, this.projection?.removedBotRefs)
    if (JSON.stringify(next) === JSON.stringify(current)) return
    this.retainBotVisibility(current, next)
    if (this.projection === undefined) this.localBots = next
    else this.projection = { ...this.projection, bots: next }
    this.commit(this.snapshot.sources)
  }

  hydrateVisibility(items: import('../types.js').ArkmeConversationDirectoryVisibilityItem[]): void {
    const current = this.projection?.visibility ?? this.localVisibility
    const visibility = new Map(current.map(item => [`${item.entryKind}:${item.entryRef}`, item]))
    for (const item of items) if (!visibility.has(`${item.entryKind}:${item.entryRef}`)) visibility.set(`${item.entryKind}:${item.entryRef}`, item)
    if (visibility.size === current.length) return
    if (this.projection === undefined) this.localVisibility = [...visibility.values()]
    else this.projection = { ...this.projection, visibility: [...visibility.values()] }
    this.commit(this.snapshot.sources)
  }

  confirmVisibility(entryKind: 'source' | 'bot', entryRef: string, hidden: boolean): void {
    const current = this.projection?.visibility ?? this.localVisibility
    const key = `${entryKind}:${entryRef}`
    const visibility = new Map(current.map(item => [`${item.entryKind}:${item.entryRef}`, item]))
    if (visibility.get(key)?.hidden === hidden) return
    visibility.set(key, { entryKind, entryRef, hidden })
    if (this.projection === undefined) this.localVisibility = [...visibility.values()]
    else this.projection = { ...this.projection, visibility: [...visibility.values()] }
    this.commit(this.snapshot.sources)
  }

  totalBadgeUnreadCount(accountScope?: string): number {
    const snapshot = this.getConversationSnapshot()
    return accountScope !== undefined && accountScope !== snapshot.accountScope ? 0 : snapshot.badgeCount
  }

  markReadOptimistic(
    source: ArkmeSourceItem,
    sourceKey: string | undefined,
    effectiveReadSequence: number,
    seedSources: readonly ArkmeSourceItem[] = [],
  ): boolean {
    const target = this.normalizedReadTarget(source.sourceRef, sourceKey ?? source.sourceKey, effectiveReadSequence)
    if (target === undefined) return false
    const indexes = { sourceKeysByRef: this.sourceKeysByRef }
    const identity = identityForSource(indexes, target.sourceRef, target.sourceKey)
    const sourceIndex = findSourceIndex(this.snapshot.sources, indexes, target.sourceRef, target.sourceKey)
    const seedSourceIndex = sourceIndex < 0 ? findSourceIndex(seedSources, indexes, target.sourceRef, target.sourceKey) : -1
    const currentSource = sourceIndex >= 0
      ? this.snapshot.sources[sourceIndex]
      : seedSourceIndex >= 0 ? seedSources[seedSourceIndex] : source
    if (currentSource === undefined
      || (normalizedCount(currentSource.unreadCount) <= 0 && currentSource.hasUnreadMention !== true)) return false
    const existing = this.optimisticReadWatermarks.get(identity)
    if (existing !== undefined && existing.effectiveReadSequence >= target.effectiveReadSequence && existing.unreadCount === 0) return false
    if (!this.optimisticUnreadBackups.has(identity)) this.optimisticUnreadBackups.set(identity, normalizedCount(currentSource.unreadCount))
    this.optimisticReadWatermarks.set(identity, { effectiveReadSequence: target.effectiveReadSequence, unreadCount: 0 })
    const sources = sourceIndex >= 0
      ? this.snapshot.sources
      : seedSources.length > 0 ? [...seedSources] : [source, ...this.snapshot.sources]
    this.commit(applyDirectoryMutations(
      sources,
      [],
      this.combinedReadWatermarks(),
      { sourceKeysByRef: this.sourceKeysByRef },
    ))
    return true
  }

  hasOptimisticRead(sourceRef: string, sourceKey: string | undefined, effectiveReadSequence: number): boolean {
    const target = this.normalizedReadTarget(sourceRef, sourceKey, effectiveReadSequence)
    if (target === undefined) return false
    const identity = identityForSource({ sourceKeysByRef: this.sourceKeysByRef }, target.sourceRef, target.sourceKey)
    const watermark = this.optimisticReadWatermarks.get(identity)
    return watermark !== undefined && watermark.effectiveReadSequence >= target.effectiveReadSequence
  }

  rejectOptimisticRead(sourceRef: string, sourceKey: string | undefined, effectiveReadSequence: number): boolean {
    const target = this.normalizedReadTarget(sourceRef, sourceKey, effectiveReadSequence)
    if (target === undefined) return false
    const indexes = { sourceKeysByRef: this.sourceKeysByRef }
    const identity = identityForSource(indexes, target.sourceRef, target.sourceKey)
    const optimistic = this.optimisticReadWatermarks.get(identity)
    if (optimistic === undefined || optimistic.effectiveReadSequence > target.effectiveReadSequence) return false
    this.optimisticReadWatermarks.delete(identity)
    const backupUnreadCount = this.optimisticUnreadBackups.get(identity)
    this.optimisticUnreadBackups.delete(identity)
    const sources = this.snapshot.sources.map(source => {
      if (backupUnreadCount === undefined) return source
      if (identityForSource(indexes, source.sourceRef, source.sourceKey) !== identity) return source
      if (normalizedSequence(source.latestSequence) > target.effectiveReadSequence) return source
      return projectSourceAttention(source, backupUnreadCount)
    })
    this.commit(applyDirectoryMutations(
      sources,
      [],
      this.combinedReadWatermarks(),
      { sourceKeysByRef: this.sourceKeysByRef },
    ))
    return true
  }

  updateReadAck(sourceRef: string, sourceKey: string | undefined, effectiveReadSequence: number, unreadCount: number): void {
    const mutation = {
      type: 'read-ack' as const,
      sourceRef,
      ...(sourceKey === undefined ? {} : { sourceKey }),
      effectiveReadSequence,
      unreadCount,
    }
    if (!this.baselineReady) {
      this.pendingMutations.push(mutation)
      recordReadWatermark(this.readWatermarks, { sourceKeysByRef: this.sourceKeysByRef }, mutation)
      this.clearOptimisticRead(mutation)
      if (this.snapshot.sources.length > 0) {
        this.commit(applyDirectoryMutations(
          this.sourcesWithReadAckUnread(mutation),
          [],
          this.combinedReadWatermarks(),
          { sourceKeysByRef: this.sourceKeysByRef },
        ))
      }
      return
    }
    recordReadWatermark(this.readWatermarks, { sourceKeysByRef: this.sourceKeysByRef }, mutation)
    this.clearOptimisticRead(mutation)
    const sources = applyDirectoryMutations(
      this.sourcesWithReadAckUnread(mutation),
      [],
      this.combinedReadWatermarks(),
      { sourceKeysByRef: this.sourceKeysByRef },
    )
    const identity = identityForSource({ sourceKeysByRef: this.sourceKeysByRef }, sourceRef, sourceKey)
    if (sources.find(item => identityForSource({ sourceKeysByRef: this.sourceKeysByRef }, item.sourceRef) === identity) === undefined) return
    this.commit(sources)
  }

  clear(): void {
    this.projection = undefined
    this.generation += 1
    this.refreshInFlight = undefined
    this.refreshedAtMillis = 0
    this.baselineReady = false
    this.isRefreshing = false
    this.pendingMutations = []
    this.readWatermarks.clear()
    this.optimisticReadWatermarks.clear()
    this.optimisticUnreadBackups.clear()
    this.sourceKeysByRef.clear()
    if (this.snapshot.sources.length > 0 || this.snapshot.baselineReady || this.snapshot.isRefreshing) this.commit([])
  }

  private setRefreshing(isRefreshing: boolean): void {
    if (this.isRefreshing === isRefreshing) return
    this.isRefreshing = isRefreshing
    this.commit(this.snapshot.sources)
  }

  private combinedReadWatermarks(): Map<string, ArkmeChatReadWatermark> {
    const combined = new Map<string, ArkmeChatReadWatermark>()
    for (const [key, watermark] of this.readWatermarks) mergeReadWatermark(combined, key, watermark)
    for (const [key, watermark] of this.optimisticReadWatermarks) mergeReadWatermark(combined, key, watermark)
    return combined
  }

  private normalizedReadTarget(
    sourceRef: string,
    sourceKey: string | undefined,
    effectiveReadSequence: number,
  ): ArkmeChatReadTarget | undefined {
    const normalizedSourceRef = sourceRef.trim()
    const normalizedSequenceValue = normalizedSequence(effectiveReadSequence)
    if (normalizedSourceRef === '' || normalizedSequenceValue <= 0) return undefined
    const normalizedSourceKeyValue = normalizedSourceKey(sourceKey)
    rememberSourceKey({ sourceKeysByRef: this.sourceKeysByRef }, normalizedSourceRef, normalizedSourceKeyValue)
    return {
      sourceRef: normalizedSourceRef,
      ...(normalizedSourceKeyValue === undefined ? {} : { sourceKey: normalizedSourceKeyValue }),
      effectiveReadSequence: normalizedSequenceValue,
    }
  }

  private clearOptimisticRead(target: ArkmeChatReadTarget): void {
    const identity = identityForSource({ sourceKeysByRef: this.sourceKeysByRef }, target.sourceRef, target.sourceKey)
    const optimistic = this.optimisticReadWatermarks.get(identity)
    if (optimistic === undefined || optimistic.effectiveReadSequence > target.effectiveReadSequence) return
    this.optimisticReadWatermarks.delete(identity)
    this.optimisticUnreadBackups.delete(identity)
  }

  private sourcesWithReadAckUnread(target: ArkmeChatReadTarget & { unreadCount: number }): ArkmeSourceItem[] {
    const indexes = { sourceKeysByRef: this.sourceKeysByRef }
    const identity = identityForSource(indexes, target.sourceRef, target.sourceKey)
    const unreadCount = normalizedCount(target.unreadCount)
    const effectiveReadSequence = normalizedSequence(target.effectiveReadSequence)
    return this.snapshot.sources.map(source => {
      if (identityForSource(indexes, source.sourceRef, source.sourceKey) !== identity) return source
      if (normalizedSequence(source.latestSequence) > effectiveReadSequence) return source
      const hasUnreadMention = unreadCount <= 0 && source.hasUnreadMention !== undefined ? false : source.hasUnreadMention
      return source.unreadCount === unreadCount && source.hasUnreadMention === hasUnreadMention
        ? source
        : projectSourceAttention({ ...source, unreadCount, ...(hasUnreadMention === undefined ? {} : { hasUnreadMention }) })
    })
  }
}

export const arkmeChatDirectory = new ArkmeChatDirectoryStore()

export interface ArkmeChatTimelineDeltaSnapshot {
  revision: number
  itemsBySourceKey: Record<string, import('../types.js').ArkmeTimelineItem[]>
}

export interface ArkmeChatTimelineSourceDeltaSnapshot {
  revision: number
  items: import('../types.js').ArkmeTimelineItem[]
  removedItemKeys: string[]
  invalidationRevision: number
  latestSequence?: number
}

const EMPTY_CHAT_TIMELINE_SOURCE_DELTA: ArkmeChatTimelineSourceDeltaSnapshot = {
  revision: 0,
  items: [],
  removedItemKeys: [],
  invalidationRevision: 0,
}
const MAX_SCOPED_REALTIME_SOURCES = 256
const MAX_TIMELINE_REMOVED_ITEM_KEYS = 512
const MAX_TIMELINE_CHANGE_CURSORS = MAX_TIMELINE_REMOVED_ITEM_KEYS * 2

type ArkmeChatTimelineChangeKind = 'deleted' | 'recovered' | 'reedited' | 'extended'

interface ArkmeChatTimelineChangeCursor {
  changeVersion: number
  changeKind: ArkmeChatTimelineChangeKind
  relationTerminal: boolean
}

function retainNewestMapEntries<Value>(map: Map<string, Value>): void {
  while (map.size > MAX_SCOPED_REALTIME_SOURCES) {
    const oldest = map.keys().next().value as string | undefined
    if (oldest === undefined) return
    map.delete(oldest)
  }
}

export class ArkmeChatTimelineDeltaStore {
  private snapshot: ArkmeChatTimelineDeltaSnapshot = { revision: 0, itemsBySourceKey: {} }
  private readonly sourceSnapshots = new Map<string, ArkmeChatTimelineSourceDeltaSnapshot>()
  private readonly changeCursorsBySource = new Map<string, Map<string, ArkmeChatTimelineChangeCursor>>()
  private accountScopeKey: string | undefined
  private readonly listeners = new Set<() => void>()
  readonly getSnapshot = (): ArkmeChatTimelineDeltaSnapshot => this.snapshot
  readonly getSnapshotForSource = (sourceKey: string): ArkmeChatTimelineSourceDeltaSnapshot => (
    this.sourceSnapshots.get(sourceKey) ?? EMPTY_CHAT_TIMELINE_SOURCE_DELTA
  )
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }
  activateAccount(scope: ArkmeClientAccountScope): void {
    const normalized = clientAccountScopeKey(scope)
    if (normalized === this.accountScopeKey) return
    this.accountScopeKey = normalized
    const hadScopedSnapshots = this.sourceSnapshots.size > 0
    this.sourceSnapshots.clear()
    this.changeCursorsBySource.clear()
    if (!hadScopedSnapshots && Object.keys(this.snapshot.itemsBySourceKey).length === 0) return
    this.snapshot = { revision: this.snapshot.revision + 1, itemsBySourceKey: {} }
    for (const listener of this.listeners) listener()
  }
  publish(updates: Array<{
    source: Pick<ArkmeSourceItem, 'sourceRef' | 'sourceKey' | 'latestSequence'>
    items: import('../types.js').ArkmeTimelineItem[]
  }>): void {
    if (updates.length === 0) {
      this.sourceSnapshots.clear()
      this.changeCursorsBySource.clear()
    }
    for (const update of updates) {
      const sourceKey = arkmeChatSourceIdentityKey(update.source)
      const previous = this.sourceSnapshots.get(sourceKey)
      const removedItemKeys = previous?.removedItemKeys ?? []
      const removedItemKeySet = new Set(removedItemKeys)
      const visibleItems = update.items.filter(item => !removedItemKeySet.has(item.timelineItemKey ?? ''))
      if (previous !== undefined && previous.latestSequence === update.source.latestSequence
        && JSON.stringify(previous.items) === JSON.stringify(visibleItems)
        && JSON.stringify(previous.removedItemKeys) === JSON.stringify(removedItemKeys)) continue
      this.sourceSnapshots.delete(sourceKey)
      this.sourceSnapshots.set(sourceKey, {
        revision: (previous?.revision ?? 0) + 1,
        items: [...visibleItems],
        removedItemKeys,
        invalidationRevision: previous?.invalidationRevision ?? 0,
        ...(update.source.latestSequence === undefined ? {} : { latestSequence: update.source.latestSequence }),
      })
    }
    retainNewestMapEntries(this.sourceSnapshots)
    this.retainChangeCursorsForKnownSources()
    this.snapshot = {
      revision: this.snapshot.revision + 1,
      itemsBySourceKey: Object.fromEntries(updates.map(update => {
        const sourceKey = arkmeChatSourceIdentityKey(update.source)
        return [sourceKey, [...(this.sourceSnapshots.get(sourceKey)?.items ?? [])]]
      })),
    }
    for (const listener of this.listeners) listener()
  }

  applyTimelineChange(change: {
    sourceKey: string
    timelineItemKey: string
    changeKind: ArkmeChatTimelineChangeKind
    changeVersion: number
    relationTerminal: boolean
    throughSequence: number
  }): void {
    const sourceKey = change.sourceKey.trim()
    const timelineItemKey = change.timelineItemKey.trim()
    if (sourceKey === '' || timelineItemKey === ''
      || !Number.isSafeInteger(change.changeVersion) || change.changeVersion <= 0) return
    const previous = this.sourceSnapshots.get(sourceKey) ?? EMPTY_CHAT_TIMELINE_SOURCE_DELTA
    const cursors = this.changeCursorsBySource.get(sourceKey) ?? new Map<string, ArkmeChatTimelineChangeCursor>()
    const previousCursor = cursors.get(timelineItemKey)
    if (!this.shouldApplyTimelineChange(
      previousCursor, change.changeVersion, change.changeKind, change.relationTerminal,
    )) return
    const removed = new Set(previous.removedItemKeys)
    if (change.changeKind === 'deleted') removed.add(timelineItemKey)
    else if (change.changeKind === 'recovered') removed.delete(timelineItemKey)
    const boundedRemovedItemKeys = [...removed].slice(-MAX_TIMELINE_REMOVED_ITEM_KEYS)
    const boundedRemovedItemKeySet = new Set(boundedRemovedItemKeys)
    cursors.delete(timelineItemKey)
    cursors.set(timelineItemKey, {
      changeVersion: change.changeVersion,
      changeKind: change.changeKind,
      relationTerminal: change.relationTerminal,
    })
    this.retainNewestChangeCursors(cursors, boundedRemovedItemKeySet)
    this.changeCursorsBySource.delete(sourceKey)
    this.changeCursorsBySource.set(sourceKey, cursors)
    this.sourceSnapshots.delete(sourceKey)
    this.sourceSnapshots.set(sourceKey, {
      revision: previous.revision + 1,
      items: previous.items.filter(item => !boundedRemovedItemKeySet.has(item.timelineItemKey ?? '')),
      removedItemKeys: boundedRemovedItemKeys,
      invalidationRevision: previous.invalidationRevision
        + (change.changeKind === 'deleted' ? 0 : 1),
      latestSequence: Math.max(previous.latestSequence ?? 0, change.throughSequence),
    })
    retainNewestMapEntries(this.sourceSnapshots)
    this.retainChangeCursorsForKnownSources()
    this.snapshot = { revision: this.snapshot.revision + 1, itemsBySourceKey: {} }
    for (const listener of this.listeners) listener()
  }

  private shouldApplyTimelineChange(
    previous: ArkmeChatTimelineChangeCursor | undefined,
    changeVersion: number,
    changeKind: ArkmeChatTimelineChangeKind,
    relationTerminal: boolean,
  ): boolean {
    if (previous?.relationTerminal === true) return false
    if (relationTerminal) return true
    if (previous === undefined) return true
    if (changeKind === previous.changeKind) return changeVersion > previous.changeVersion
    const isRecordLifecyclePair = (changeKind === 'deleted' || changeKind === 'recovered')
      && (previous.changeKind === 'deleted' || previous.changeKind === 'recovered')
    if (!isRecordLifecyclePair) return true
    if (changeVersion !== previous.changeVersion) return changeVersion > previous.changeVersion
    return changeKind === 'deleted'
  }

  private retainNewestChangeCursors(
    cursors: Map<string, ArkmeChatTimelineChangeCursor>,
    removedItemKeys: Set<string>,
  ): void {
    while (cursors.size > MAX_TIMELINE_CHANGE_CURSORS) {
      const oldestNonRemoved = [...cursors.keys()].find(key => !removedItemKeys.has(key))
      if (oldestNonRemoved === undefined) return
      cursors.delete(oldestNonRemoved)
    }
  }

  private retainChangeCursorsForKnownSources(): void {
    for (const sourceKey of this.changeCursorsBySource.keys()) {
      if (!this.sourceSnapshots.has(sourceKey)) this.changeCursorsBySource.delete(sourceKey)
    }
  }
}

export const arkmeChatTimelineDelta = new ArkmeChatTimelineDeltaStore()

export interface ArkmeInterwovenInvalidationSnapshot {
  revision: number
}

/** Realtime frames are invalidation hints only; interwoven content always comes from its owner read endpoint. */
export class ArkmeInterwovenInvalidationStore {
  private snapshot: ArkmeInterwovenInvalidationSnapshot = { revision: 0 }
  private accountScopeKey: string | undefined
  private nextScopedRevision = 0
  private globalRevision = 0
  private readonly sourceRevisions = new Map<string, number>()
  private readonly sourceSnapshots = new Map<string, { globalRevision: number; sourceRevision: number; snapshot: ArkmeInterwovenInvalidationSnapshot }>()
  private readonly listeners = new Set<() => void>()
  readonly getSnapshot = (): ArkmeInterwovenInvalidationSnapshot => this.snapshot
  readonly getSnapshotForSource = (sourceKey: string): ArkmeInterwovenInvalidationSnapshot => {
    const sourceRevision = this.sourceRevisions.get(sourceKey) ?? 0
    const cached = this.sourceSnapshots.get(sourceKey)
    if (cached?.globalRevision === this.globalRevision && cached.sourceRevision === sourceRevision) return cached.snapshot
    const snapshot = { revision: Math.max(this.globalRevision, sourceRevision) }
    this.sourceSnapshots.delete(sourceKey)
    this.sourceSnapshots.set(sourceKey, { globalRevision: this.globalRevision, sourceRevision, snapshot })
    retainNewestMapEntries(this.sourceSnapshots)
    return snapshot
  }
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }
  activateAccount(scope: ArkmeClientAccountScope): void {
    const normalized = clientAccountScopeKey(scope)
    if (normalized === this.accountScopeKey) return
    this.accountScopeKey = normalized
    this.nextScopedRevision = 0
    this.globalRevision = 0
    this.sourceRevisions.clear()
    this.sourceSnapshots.clear()
    this.snapshot = { revision: this.snapshot.revision + 1 }
    for (const listener of this.listeners) listener()
  }
  invalidate(sourceKey?: string): void {
    if (sourceKey === undefined || sourceKey.trim() === '') {
      this.globalRevision = ++this.nextScopedRevision
      this.sourceSnapshots.clear()
    } else {
      const normalized = sourceKey.trim()
      const revision = ++this.nextScopedRevision
      this.sourceRevisions.delete(normalized)
      this.sourceRevisions.set(normalized, revision)
      this.sourceSnapshots.delete(normalized)
      retainNewestMapEntries(this.sourceRevisions)
    }
    this.snapshot = { revision: this.snapshot.revision + 1 }
    for (const listener of this.listeners) listener()
  }
}

export const arkmeInterwovenInvalidation = new ArkmeInterwovenInvalidationStore()
